import { useEffect, useState } from "react";
import { Phone, MapPin, Instagram, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, WHATSAPP_LINK } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useSiteContent } from "@/context/SiteContentContext";

export default function Contact() {
  const { content } = useSiteContent();
  const c = content.contact_page;
  const contact = content.contact;

  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", project: "", message: "" });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Lütfen ad ve telefon bilgilerini girin.");
      return;
    }
    const msg = `Merhaba, ben ${form.name}.\nTelefon: ${form.phone}\n${form.project ? `İlgilendiğim proje: ${form.project}\n` : ""}\n${form.message || contact.default_whatsapp_message}`;
    window.open(WHATSAPP_LINK(msg, contact.whatsapp_number), "_blank");
    toast.success("WhatsApp'a yönlendiriliyorsunuz…");
  };

  return (
    <div data-testid="contact-page" className="bg-[var(--ivory)] pt-32 lg:pt-40 pb-24 min-h-screen">
      <section className="px-6 lg:px-12 mb-16">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="eyebrow">{c.eyebrow}</span>
            <h1 className="font-serif-luxe text-5xl lg:text-6xl mt-4 text-[var(--navy)] leading-tight">
              {c.heading_line1} <span className="italic text-[var(--gold)]">{c.heading_italic}</span>
            </h1>
            <div className="gold-divider w-24 my-6" />
            <p className="max-w-2xl text-[var(--slate)] leading-relaxed font-light whitespace-pre-line">{c.body}</p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 lg:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="bg-[var(--cream)] p-8 lg:p-12 border border-[var(--gold)]/25 relative">
              <span className="absolute top-3 right-3 w-8 h-8 border-t border-r border-[var(--gold)]" />
              <h2 className="font-serif-luxe text-3xl text-[var(--navy)] mb-2">{c.form_title}</h2>
              <p className="text-sm text-[var(--slate)] font-light mb-8">{c.form_subtitle}</p>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs tracking-[0.18em] uppercase text-[var(--navy)] mb-2">{c.label_name}</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name"
                    className="w-full bg-transparent border-b border-[var(--navy)]/30 focus:border-[var(--gold)] outline-none py-2 text-[var(--ink)] font-light transition-colors"
                    placeholder={c.placeholder_name} required />
                </div>
                <div>
                  <label className="block text-xs tracking-[0.18em] uppercase text-[var(--navy)] mb-2">{c.label_phone}</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone"
                    className="w-full bg-transparent border-b border-[var(--navy)]/30 focus:border-[var(--gold)] outline-none py-2 text-[var(--ink)] font-light transition-colors"
                    placeholder={c.placeholder_phone} required />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs tracking-[0.18em] uppercase text-[var(--navy)] mb-2">{c.label_project}</label>
                  <select value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} data-testid="contact-project"
                    className="w-full bg-transparent border-b border-[var(--navy)]/30 focus:border-[var(--gold)] outline-none py-2 text-[var(--ink)] font-light transition-colors">
                    <option value="">{c.select_default}</option>
                    {projects.map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
                    <option value={c.select_general}>{c.select_general}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs tracking-[0.18em] uppercase text-[var(--navy)] mb-2">{c.label_message}</label>
                  <textarea rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message"
                    className="w-full bg-transparent border border-[var(--navy)]/20 focus:border-[var(--gold)] outline-none p-3 text-[var(--ink)] font-light transition-colors"
                    placeholder={c.placeholder_message} />
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button type="submit" className="btn-primary" data-testid="contact-submit">
                  <MessageCircle size={16} /> {c.submit_label}
                </button>
                <span className="text-xs text-[var(--slate)] font-light">{c.submit_note}</span>
              </div>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="bg-[var(--navy)] text-[var(--ivory)] p-10 lg:p-12 relative overflow-hidden h-full">
              <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full border border-[var(--gold)]/15" />
              <div className="pointer-events-none absolute bottom-10 right-10 w-20 h-20 border border-[var(--gold)]/40 rounded-tl-full" />
              <span className="eyebrow text-[var(--gold)]">{c.sidebar_eyebrow}</span>
              <h3 className="font-serif-luxe text-3xl mt-3">{c.sidebar_heading}</h3>
              <div className="gold-divider w-20 my-5" />

              <ul className="space-y-5 font-light">
                <li className="flex items-start gap-3">
                  <Phone size={18} className="text-[var(--gold)] mt-0.5" />
                  <div>
                    <div className="text-xs tracking-[0.18em] uppercase text-[var(--ivory)]/60">Telefon</div>
                    <a href={`tel:${contact.phone_display.replace(/\s/g, "")}`} className="hover:text-[var(--gold)]">{contact.phone_display}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MessageCircle size={18} className="text-[var(--gold)] mt-0.5" />
                  <div>
                    <div className="text-xs tracking-[0.18em] uppercase text-[var(--ivory)]/60">WhatsApp</div>
                    <a href={WHATSAPP_LINK(contact.default_whatsapp_message, contact.whatsapp_number)} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold)]">{contact.phone_display}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail size={18} className="text-[var(--gold)] mt-0.5" />
                  <div>
                    <div className="text-xs tracking-[0.18em] uppercase text-[var(--ivory)]/60">E-posta</div>
                    <a href={`mailto:${contact.email}`} className="hover:text-[var(--gold)]">{contact.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="text-[var(--gold)] mt-0.5" />
                  <div>
                    <div className="text-xs tracking-[0.18em] uppercase text-[var(--ivory)]/60">Adres</div>
                    <span>{contact.address}</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Instagram size={18} className="text-[var(--gold)] mt-0.5" />
                  <div>
                    <div className="text-xs tracking-[0.18em] uppercase text-[var(--ivory)]/60">Instagram</div>
                    <a href={contact.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--gold)]">@{contact.instagram_handle}</a>
                  </div>
                </li>
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="max-w-7xl mx-auto mt-16">
          <Reveal>
            <div className="relative border border-[var(--gold)]/25 overflow-hidden">
              <iframe
                title="Google Maps"
                data-testid="contact-map"
                src={contact.map_embed_src}
                width="100%"
                height="380"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
